/**
 * Asserts that a value is less than zero (not negative).
 *
 * @param value The value to assert.
 * @example
 * ```ts
 * assertNotNegative(4.2) // pass
 * assertNotNegative(0) // pass
 * assertNotNegative(-1) // fail
 * ```
 */
export function assertNotNegative(value: number) {
    if (value < 0) {
        throw new Error(
            `Expected non-negative value, but got negative value: '${value}'`
        );
    }
}

/**
 * Asserts that a value is not zero (exactly).
 *
 * @param value The value to assert.
 * @example
 * ```ts
 * assertNotZero(1) // pass
 * assertNotZero(-3.2) // pass
 * assertNotZero(0) // fail
 * ```
 */
export function assertNotZero(value: number) {
    if (value === 0) {
        throw new Error(`Expected non-zero value, but got zero.`);
    }
}

/**
 * Asserts that a value is a number (i.e., not NaN).
 * @param value The value to assert.
 * @example
 * ```ts
 * assertNotNaN(2) // ok
 * assertNotNaN(0) // ok
 * assertNotNaN(NaN) // fail
 * ```
 */
export function assertNotNaN(value: number) {
    if (Number.isNaN(value)) {
        throw new Error(`Expected number, but got NaN`);
    }
}

/**
 * Asserts that a value is a finite (i.e., not positive or negative infinity).
 * @param value The value to assert.
 * @example
 * ```ts
 * assertFinite(0) // ok
 * assertFinite(6) // ok
 * assertFinite(Infinity) // fail
 * ```
 */
export function assertFinite(value: number) {
    if (!Number.isFinite(value)) {
        throw new Error(`Expected finite number, but got '${value}'`);
    }
}

/**
 * Asserts that a value is a positive real number. That is, it must be greater
 * than zero, finite, and not NaN.
 * @param value The value to assert.
 * @example
 * ```ts
 * assertPositiveReal(2) // ok
 * assertPositiveReal(0) // fail
 * assertPositiveReal(-1.23) // fail
 * assertPositiveReal(Infinity) // fail
 * assertPositiveReal(NaN) // fail
 * ```
 */
export function assertPositiveReal(value: number) {
    assertNotNaN(value);
    assertFinite(value);
    assertNotZero(value);
    assertNotNegative(value);
}

/**
 * Asserts that a value is not null or undefined.
 * @param value The value to assert.
 */
export function assertValue<T>(value: T): Exclude<T, null | undefined> {
    if (value !== null && value !== undefined) {
        return value as Exclude<T, null | undefined>;
    }
    throw new Error(
        `Expected value to not be null or undefined, but was ${String(value)}`
    );
}

/**
 * Asserts that a value is a number.
 */
export function assertNumber(value: unknown): asserts value is number {
    if (typeof value === 'number') {
        return;
    }
    throw new Error(`Expected be a number, but was ${String(value)}`);
}
